import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useContactPopup } from '@/hooks/useContactPopup';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { getGeoLocation, submitLead } from '@/services/api';
import { useNavigate } from 'react-router-dom';
import { countryCodes } from '@/data/countryCodes';

export const ContactPopup = () => {
  const { isOpen, closePopup } = useContactPopup();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+44',
    phone: '',
    message: ''
  });
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const result = await getGeoLocation();
      if (result.success) {
        setGeoData(result.data);
        console.log('Geolocation data:', result.data);
      }
    };
    
    if (isOpen) {
      fetchGeoLocation();
    }
  }, [isOpen]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Only allow digits and must be exactly 10 digits
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handlePhoneChange = (value) => {
    // Only allow digits and max 10 characters
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: digitsOnly });
    
    // Validate on change
    if (digitsOnly && !validatePhone(digitsOnly)) {
      setErrors({ ...errors, phone: 'Phone number must be exactly 10 digits' });
    } else {
      setErrors({ ...errors, phone: '' });
    }
  };

  const handleEmailChange = (value) => {
    setFormData({ ...formData, email: value });
    
    // Validate on change
    if (value && !validateEmail(value)) {
      setErrors({ ...errors, email: 'Please enter a valid email address (e.g., user@example.com)' });
    } else {
      setErrors({ ...errors, email: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation
    const newErrors = {};
    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit lead data
      const result = await submitLead(formData, geoData);
      
      if (result.success) {
        toast.success(result.message || 'Thank you! We\'ll contact you shortly.');
        setFormData({ name: '', email: '', countryCode: '+44', phone: '', message: '' });
        setErrors({ email: '', phone: '' });
        closePopup();
        
        // Redirect to application page with customer ID
        navigate(`/application/main?customerid=${result.customerId}`);
      } else {
        toast.error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={closePopup}>
      <DialogContent className="max-w-[900px] p-0 overflow-hidden">
        <div className="grid md:grid-cols-2 gap-0">
          {/* Left Side - Image */}
          <div className="hidden md:block relative h-full min-h-[600px]">
            <img
              src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&h=900&fit=crop&q=80"
              alt="Trademark Registration Services"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#012169]/80 to-[#C8102E]/60 flex items-center justify-center p-8">
              <div className="text-center text-white">
                <h3 className="text-3xl font-bold mb-4">Protect Your Brand Today</h3>
                <p className="text-lg opacity-90">Join 10,000+ UK businesses who secured their trademarks with us</p>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="p-8">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-2xl font-bold text-primary">Get Started with Trademark Registration</DialogTitle>
              <DialogDescription>
                Fill out the form and our experts will contact you within 24 hours.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="popup-name">Name *</Label>
                <Input
                  id="popup-name"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="popup-email">Email *</Label>
                <Input
                  id="popup-email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  required
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="popup-phone">Contact Number *</Label>
                <div className="flex gap-2">
                  <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
                    <SelectTrigger className="w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countryCodes.map((item) => (
                        <SelectItem key={item.code} value={item.code}>
                          {item.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex-1 space-y-2">
                    <Input
                      id="popup-phone"
                      type="tel"
                      placeholder="1234567890 (10 digits)"
                      value={formData.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      required
                      maxLength={10}
                      className={errors.phone ? 'border-red-500' : ''}
                    />
                    {errors.phone && (
                      <p className="text-sm text-red-500">{errors.phone}</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="popup-message">Description</Label>
                <Textarea
                  id="popup-message"
                  placeholder="Tell us about your trademark needs..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="flex-1 bg-gradient-to-r from-[#012169] to-[#C8102E] hover:from-[#011354] hover:to-[#a80d25] text-white disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
                <Button type="button" variant="outline" onClick={closePopup} className="border-2">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactPopup;
